import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import type { BlogPost, BlogCategoria, PostStatus } from '@/types/blog';
import { supabase } from '@/integrations/supabase/client';
import { useOfertaCompleto } from '@/contexts/OfertaContext';
import { completeCommercialText } from '@/config/completoContent';
import { isHiddenTool } from '@/lib/completoPolicy';

// Load the editorial dataset only inside the blog provider.

interface BlogContextType {
  posts: BlogPost[];
  categorias: BlogCategoria[];
  publicados: BlogPost[];
  loading: boolean;
  createPost: (post: Omit<BlogPost, 'id' | 'dataCriacao' | 'dataAtualizacao'>) => void;
  updatePost: (id: string, data: Partial<BlogPost>) => void;
  deletePost: (id: string) => void;
  setPostStatus: (id: string, status: PostStatus) => void;
  addCategoria: (cat: BlogCategoria) => void;
}

const BlogContext = createContext<BlogContextType | undefined>(undefined);

function mapPostFromDb(row: any): BlogPost {
  return {
    id: row.id,
    titulo: row.titulo,
    slug: row.slug,
    subtitulo: row.subtitulo,
    resumo: row.resumo,
    conteudo: row.conteudo,
    imagemCapa: row.imagem_capa,
    imagensInternas: row.imagens_internas,
    tags: row.tags || [],
    categoria: row.categoria,
    categoriaSlug: row.categoria_slug,
    status: row.status as PostStatus,
    dataPublicacao: row.data_publicacao,
    dataCriacao: row.data_criacao,
    dataAtualizacao: row.data_atualizacao,
  };
}

async function seedFromConteudos(): Promise<BlogPost[]> {
  const { conteudos } = await import('@/data/conteudos');
  const now = new Date().toISOString();
  return conteudos.map((c, i) => ({
    id: `seed-${i}`,
    titulo: c.titulo,
    slug: c.slug,
    resumo: c.resumo,
    conteudo: c.conteudo,
    categoria: c.categoria,
    categoriaSlug: c.categoriaSlug,
    tags: [],
    status: 'publicado' as PostStatus,
    dataPublicacao: now,
    dataCriacao: now,
    dataAtualizacao: now,
    metaTitle: c.metaTitle,
    metaDescription: c.metaDescription,
    faq: c.faq?.map(f => ({ pergunta: f.pergunta, resposta: f.resposta })),
    internalLinks: c.internalLinks,
    internalLinkLabels: c.internalLinkLabels,
    cta: c.cta,
  }));
}

export const BlogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const completo = useOfertaCompleto();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [categorias, setCategorias] = useState<BlogCategoria[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      // Defer the default categorias dataset to avoid pulling conteudos.ts at startup.
      import('@/data/conteudos').then(({ categorias: defaultCategorias }) => {
        setCategorias((prev) => (prev.length ? prev : defaultCategorias));
      });

      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .order('data_criacao', { ascending: false });

      const editorialPosts = await seedFromConteudos();
      const dbPosts = !error && data ? data.map(mapPostFromDb) : [];
      const editorialBySlug = new Map(editorialPosts.map(post => [post.slug, post]));
      const dbSlugs = new Set(dbPosts.map(post => post.slug));
      setPosts([
        ...dbPosts.map(post => {
          const editorial = editorialBySlug.get(post.slug);
          return {
            ...post,
            metaTitle: editorial?.metaTitle,
            metaDescription: editorial?.metaDescription,
            faq: editorial?.faq,
            internalLinks: editorial?.internalLinks,
            internalLinkLabels: editorial?.internalLinkLabels,
            cta: editorial?.cta,
          };
        }),
        ...editorialPosts.filter(post => !dbSlugs.has(post.slug)),
      ]);
      setLoading(false);
    };

    load();
  }, []);

  const publicados = useMemo(() => posts.filter(p => p.status === 'publicado' && (!completo || !isHiddenTool(p.slug))).map(p => !completo ? p : ({ ...p, resumo: completeCommercialText(p.resumo), conteudo: completeCommercialText(p.conteudo), metaTitle: p.metaTitle ? completeCommercialText(p.metaTitle) : p.metaTitle, metaDescription: p.metaDescription ? completeCommercialText(p.metaDescription) : p.metaDescription, cta: p.cta ? completeCommercialText(p.cta) : p.cta, faq: p.faq?.map(item => ({ pergunta: completeCommercialText(item.pergunta), resposta: completeCommercialText(item.resposta) })) })), [posts, completo]);

  const createPost = useCallback((post: Omit<BlogPost, 'id' | 'dataCriacao' | 'dataAtualizacao'>) => {
    const now = new Date().toISOString();
    const id = crypto.randomUUID();
    const newPost: BlogPost = { ...post, id, dataCriacao: now, dataAtualizacao: now };
    setPosts(prev => [newPost, ...prev]);

    supabase.from('blog_posts').insert({
      id, titulo: post.titulo, slug: post.slug, subtitulo: post.subtitulo,
      resumo: post.resumo, conteudo: post.conteudo,
      imagem_capa: post.imagemCapa, imagens_internas: post.imagensInternas,
      tags: post.tags, categoria: post.categoria, categoria_slug: post.categoriaSlug,
      status: post.status, data_publicacao: post.dataPublicacao,
    }).then();
  }, []);

  const updatePost = useCallback((id: string, data: Partial<BlogPost>) => {
    const now = new Date().toISOString();
    setPosts(prev => prev.map(p =>
      p.id === id ? { ...p, ...data, dataAtualizacao: now } : p
    ));

    const dbData: Record<string, any> = { data_atualizacao: now };
    if (data.titulo !== undefined) dbData.titulo = data.titulo;
    if (data.slug !== undefined) dbData.slug = data.slug;
    if (data.subtitulo !== undefined) dbData.subtitulo = data.subtitulo;
    if (data.resumo !== undefined) dbData.resumo = data.resumo;
    if (data.conteudo !== undefined) dbData.conteudo = data.conteudo;
    if (data.imagemCapa !== undefined) dbData.imagem_capa = data.imagemCapa;
    if (data.imagensInternas !== undefined) dbData.imagens_internas = data.imagensInternas;
    if (data.tags !== undefined) dbData.tags = data.tags;
    if (data.categoria !== undefined) dbData.categoria = data.categoria;
    if (data.categoriaSlug !== undefined) dbData.categoria_slug = data.categoriaSlug;
    if (data.status !== undefined) dbData.status = data.status;
    if (data.dataPublicacao !== undefined) dbData.data_publicacao = data.dataPublicacao;
    supabase.from('blog_posts').update(dbData as any).eq('id', id).then();
  }, []);

  const deletePost = useCallback((id: string) => {
    setPosts(prev => prev.filter(p => p.id !== id));
    supabase.from('blog_posts').delete().eq('id', id).then();
  }, []);

  const setPostStatus = useCallback((id: string, status: PostStatus) => {
    const now = new Date().toISOString();
    setPosts(prev => prev.map(p =>
      p.id === id
        ? { ...p, status, dataAtualizacao: now, ...(status === 'publicado' && !p.dataPublicacao ? { dataPublicacao: now } : {}) }
        : p
    ));
    const dbData: Record<string, any> = { status, data_atualizacao: now };
    supabase.from('blog_posts').update(dbData as any).eq('id', id).then();
  }, []);

  const addCategoria = useCallback((cat: BlogCategoria) => {
    setCategorias(prev => {
      if (prev.some(c => c.slug === cat.slug)) return prev;
      return [...prev, cat];
    });
  }, []);

  return (
    <BlogContext.Provider value={{ posts, categorias, publicados, loading, createPost, updatePost, deletePost, setPostStatus, addCategoria }}>
      {children}
    </BlogContext.Provider>
  );
};

export const useBlog = () => {
  const ctx = useContext(BlogContext);
  if (!ctx) throw new Error('useBlog must be used within BlogProvider');
  return ctx;
};
